import * as React from "react";
// Rohtext statt eines separat gepflegten Auszugs, damit Release Notes und "Neu in dieser
// Version"-Dialog automatisch mit CHANGELOG.md synchron bleiben und nirgendwo doppelt gepflegt
// werden müssen - gemeinsame Datenquelle für ReleaseNotesDialog.tsx UND WhatsNewDialog.tsx
// (App-Konsistenz-Audit gegen TranscriptWebapp, Thema 3).
import changelogRaw from "../../../../CHANGELOG.md?raw";

export interface ChangelogSection {
  heading: string;
  items: string[];
}

export interface ChangelogEntry {
  version: string;
  date: string;
  summary: string | null;
  sections: ChangelogSection[];
}

/**
 * Minimaler, auf das feste CHANGELOG.md-Format zugeschnittener Parser (Versionsüberschrift,
 * optionale einzeilige Zusammenfassung, "### Kategorie"-Abschnitte, Aufzählungspunkte inkl.
 * umgebrochener Folgezeilen) - bewusst ohne allgemeine Markdown-Bibliothek, da das Format hier
 * vollständig unter eigener Kontrolle steht.
 */
function parseChangelog(raw: string): ChangelogEntry[] {
  const entries: ChangelogEntry[] = [];
  let current: ChangelogEntry | null = null;
  let currentSection: ChangelogSection | null = null;

  for (const line of raw.split("\n")) {
    const versionMatch = line.match(/^## \[([^\]]+)\] - (.+)$/);
    if (versionMatch) {
      current = { version: versionMatch[1], date: versionMatch[2], summary: null, sections: [] };
      entries.push(current);
      currentSection = null;
      continue;
    }
    if (!current) continue; // Einleitungstext vor der ersten Version überspringen.

    const sectionMatch = line.match(/^### (.+)$/);
    if (sectionMatch) {
      currentSection = { heading: sectionMatch[1], items: [] };
      current.sections.push(currentSection);
      continue;
    }

    const bulletMatch = line.match(/^- (.+)$/);
    if (bulletMatch) {
      currentSection?.items.push(bulletMatch[1]);
      continue;
    }

    const trimmed = line.trim();
    if (!trimmed) continue;

    if (!currentSection && !current.summary) {
      current.summary = trimmed;
      continue;
    }
    // Umgebrochene Folgezeile eines Aufzählungspunkts - an den letzten Punkt anhängen.
    if (currentSection && currentSection.items.length > 0) {
      currentSection.items[currentSection.items.length - 1] += ` ${trimmed}`;
    }
  }

  return entries;
}

/** Rendert **fett** und `code` innerhalb eines Aufzählungspunkts, ohne allgemeine Markdown-Unterstützung. */
export function renderInline(text: string): React.ReactNode {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return React.createElement("strong", { key: i }, part.slice(2, -2));
    if (part.startsWith("`") && part.endsWith("`")) {
      return React.createElement(
        "code",
        { key: i, className: "rounded bg-slate-100 px-1 py-0.5 text-[0.85em] dark:bg-slate-800" },
        part.slice(1, -1),
      );
    }
    return part;
  });
}

export const RELEASE_NOTES = parseChangelog(changelogRaw);
